/* CENTEC hybrid search. Shopify handles general queries; partial search supplements numeric and zero-result queries. */
window.CentecSearchMode = 'hybrid';
/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(() => {
  'use strict';
  if (window.CentecStockOrder) return;
  const gridSelector = '[data-centec-stock-grid]';
  const cardsIn = grid => Array.from(grid.children).filter(node => node.matches('product-item'));
  const soldOut = card => card.classList.contains('product-item--sold-out');
  function sortVisible(root) {
    root.querySelectorAll('.product-list__inner').forEach(grid => {
      if (grid.matches(gridSelector)) return;
      const cards = cardsIn(grid);
      const sorted = cards.filter(card => !soldOut(card)).concat(cards.filter(soldOut));
      if (cards.some((card, i) => card !== sorted[i])) sorted.forEach(card => grid.append(card));
    });
  }
  function pagePlan(page, size, available, unavailable) {
    const start = (page - 1) * size;
    const stop = Math.min(start + size, available + unavailable);
    const plans = [];
    for (const [value, base, count] of [['1', 0, available], ['0', available, unavailable]]) {
      let offset = Math.max(start, base) - base;
      const end = Math.min(stop, base + count) - base;
      while (offset < end) {
        const localPage = Math.floor(offset / size) + 1;
        const from = offset % size;
        const take = Math.min(size - from, end - offset);
        plans.push({ value, page: localPage, from, take });
        offset += take;
      }
    }
    return plans;
  }
  async function prepare(root, address, outerSignal) {
    // Shopify already applies unavailable_products=last to native search, including pagination and filters.
    if (['native','hybrid'].includes(window.CentecSearchMode) && /\/search\/?$/.test(new URL(address, location.href).pathname)) return;
    const grid = root.querySelector(gridSelector);
    if (!grid || grid.dataset.stockReady) return;
    const url = new URL(address, location.href);
    const chosen = new Set(url.searchParams.getAll('filter.v.availability').flatMap(value => value.split(',')));
    if (chosen.size === 1 && (chosen.has('0') || chosen.has('1'))) return;
    const page = Number(grid.dataset.stockPage);
    const size = Number(grid.dataset.stockSize);
    const total = Number(grid.dataset.stockTotal);
    const available = Number(grid.dataset.stockAvailable);
    const unavailable = Number(grid.dataset.stockUnavailable);
    if (![page, size, total, available, unavailable].every(Number.isInteger) || page < 1 || size < 1 || available < 0 || unavailable < 0 || available + unavailable !== total) return;
    if (!available || !unavailable) { grid.dataset.stockReady = 'true'; return; }
    const facet = grid.closest('product-facet');
    const sectionId = facet && facet.getAttribute('section-id');
    if (!sectionId) return;
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (outerSignal?.aborted) return;
    outerSignal?.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, 8000);
    grid.setAttribute('aria-busy', 'true');
    try {
      const plan = pagePlan(page, size, available, unavailable);
      const groups = await Promise.all(plan.map(async part => {
        const request = new URL(url);
        request.searchParams.delete('sections');
        request.searchParams.set('section_id', sectionId);
        request.searchParams.set('filter.v.availability', part.value);
        request.searchParams.set('page', String(part.page));
        if (/\/search$/.test(request.pathname)) request.searchParams.set('options[unavailable_products]', 'last');
        const response = await fetch(request, { signal: controller.signal });
        if (!response.ok) throw new Error('Stock page request failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fetchedGrid = doc.querySelector(gridSelector);
        if (!fetchedGrid) throw new Error('Stock grid missing');
        const fetched = cardsIn(fetchedGrid).slice(part.from, part.from + part.take);
        if (fetched.length !== part.take || fetched.some(card => soldOut(card) !== (part.value === '0'))) throw new Error('Stock changed during request');
        return fetched;
      }));
      if (controller.signal.aborted) return;
      const cards = groups.flat();
      const identities = cards.map(card => card.querySelector('a[href*="/products/"]')?.getAttribute('href')?.split('?')[0]);
      if (identities.some(id => !id) || new Set(identities).size !== identities.length) throw new Error('Duplicate stock results');
      cards.forEach(card => card.removeAttribute('reveal'));
      const styles = Array.from(grid.children).filter(node => node.tagName === 'STYLE');
      grid.replaceChildren(...styles.slice(0, 1), ...cards);
      grid.dataset.stockReady = 'true';
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('CENTEC: se conserva el listado original porque no se pudo completar el orden por disponibilidad.', error);
    } finally {
      clearTimeout(timer);
      outerSignal?.removeEventListener('abort', abort);
      grid.removeAttribute('aria-busy');
    }
  }
  window.CentecStockOrder = { prepare, pagePlan };
  function start() {
    prepare(document, location.href);
    sortVisible(document);
    let pending = false;
    new MutationObserver(records => {
      if (pending || !records.some(record => Array.from(record.addedNodes).some(node => node.nodeType === 1 && (node.matches?.('product-item, product-list') || node.querySelector?.('product-item'))))) return;
      pending = true;
      requestAnimationFrame(() => { pending = false; sortVisible(document); });
    }).observe(document.body, { childList: true, subtree: true });
    document.addEventListener('submit', event => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !/\/search\/?$/.test(new URL(form.action, location.href).pathname)) return;
      let input = form.querySelector('[name="options[unavailable_products]"]');
      if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = 'options[unavailable_products]'; form.append(input); }
      input.value = 'last';
    }, true);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();



  (function () {
    if (window.__centecProductCardGalleryReady) return;
    window.__centecProductCardGalleryReady = true;

    function getGalleryData(wrapper) {
      if (!wrapper) return [];
      if (wrapper.__centecGalleryData) return wrapper.__centecGalleryData;

      var dataNode = wrapper.querySelector('.centec-card-gallery__data');
      if (!dataNode) return [];

      try {
        wrapper.__centecGalleryData = JSON.parse(dataNode.textContent || '[]');
      } catch (error) {
        wrapper.__centecGalleryData = [];
      }

      return wrapper.__centecGalleryData;
    }

    function preloadImage(src) {
      if (!src) return;
      var preload = new Image();
      preload.src = src;
    }

    function updateGalleryPosition(wrapper, currentIndex, total) {
      if (!wrapper || total < 2) return;

      var position = wrapper.querySelector('[data-centec-gallery-position]');
      if (!position) return;

      var maxDots = 6;
      var visibleDots = Math.min(total, maxDots);
      var activeDot = Math.min(currentIndex, maxDots - 1);

      position.innerHTML = '';
      position.classList.remove('is-many', 'is-very-many');

      for (var i = 0; i < visibleDots; i++) {
        var dot = document.createElement('span');
        dot.className = 'centec-card-gallery__dot' + (i === activeDot ? ' is-active' : '');
        position.appendChild(dot);
      }
    }

    function initializeGalleryPositions(root) {
      var scope = root && root.querySelectorAll ? root : document;

      scope.querySelectorAll('[data-centec-card-gallery]').forEach(function (wrapper) {
        var gallery = getGalleryData(wrapper);
        if (gallery.length < 2) return;

        var currentIndex = parseInt(wrapper.dataset.centecGalleryIndex || '0', 10);
        updateGalleryPosition(wrapper, currentIndex, gallery.length);
      });
    }

    function showGalleryImage(wrapper, requestedIndex) {
      var gallery = getGalleryData(wrapper);
      var image = wrapper ? wrapper.querySelector('.centec-card-gallery__image') : null;

      if (!image || gallery.length < 2) return;

      var total = gallery.length;
      var index = ((requestedIndex % total) + total) % total;
      var item = gallery[index];

      if (!item || !item.src) return;

      wrapper.dataset.centecGalleryIndex = String(index);
      updateGalleryPosition(wrapper, index, total);
      image.classList.add('is-changing');

      var loader = new Image();

      loader.onload = function () {
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = item.src;
        image.alt = item.alt || '';
        image.dataset.mediaId = String(item.id || '');

        requestAnimationFrame(function () {
          image.classList.remove('is-changing');
        });

        var nextItem = gallery[(index + 1) % total];
        if (nextItem) preloadImage(nextItem.src);
      };

      loader.onerror = function () {
        image.classList.remove('is-changing');
      };

      loader.src = item.src;
    }

    function bindGalleryButtons(root) {
      var scope = root && root.querySelectorAll ? root : document;

      scope.querySelectorAll('[data-centec-gallery-prev], [data-centec-gallery-next]').forEach(function (button) {
        if (button.dataset.centecGalleryBound === 'true') return;
        button.dataset.centecGalleryBound = 'true';

        /* Evita que el clic llegue al enlace del producto o al botón Añadir que vive en la misma zona */
        ['pointerdown', 'mousedown', 'touchstart'].forEach(function (eventName) {
          button.addEventListener(eventName, function (event) {
            event.stopPropagation();
          }, { capture: true, passive: true });
        });

        button.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          var wrapper = button.closest('[data-centec-card-gallery]');
          var gallery = getGalleryData(wrapper);
          if (!wrapper || gallery.length < 2) return false;

          var currentIndex = parseInt(wrapper.dataset.centecGalleryIndex || '0', 10);
          var direction = button.hasAttribute('data-centec-gallery-next') ? 1 : -1;

          showGalleryImage(wrapper, currentIndex + direction);
          return false;
        }, true);
      });
    }


    function bindGallerySwipe(root) {
      var scope = root && root.querySelectorAll ? root : document;

      scope.querySelectorAll('[data-centec-card-gallery]').forEach(function (wrapper) {
        if (wrapper.dataset.centecSwipeBound === 'true') return;

        var gallery = getGalleryData(wrapper);
        var media = wrapper.querySelector('.centec-card-gallery__media');
        var link = wrapper.querySelector('.centec-card-gallery__link');

        if (!media || gallery.length < 2) return;

        wrapper.dataset.centecSwipeBound = 'true';

        var startX = 0;
        var startY = 0;
        var lastX = 0;
        var lastY = 0;
        var tracking = false;
        var horizontalGesture = false;

        media.addEventListener('touchstart', function (event) {
          if (!event.touches || event.touches.length !== 1) return;

          var touch = event.touches[0];
          startX = touch.clientX;
          startY = touch.clientY;
          lastX = startX;
          lastY = startY;
          tracking = true;
          horizontalGesture = false;
        }, { passive: true });

        media.addEventListener('touchmove', function (event) {
          if (!tracking || !event.touches || event.touches.length !== 1) return;

          var touch = event.touches[0];
          lastX = touch.clientX;
          lastY = touch.clientY;

          var deltaX = lastX - startX;
          var deltaY = lastY - startY;

          if (Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY) * 1.15) {
            horizontalGesture = true;

            /* El gesto horizontal pertenece a la mini galería.
               El scroll vertical de la página sigue funcionando normalmente. */
            if (event.cancelable) {
              event.preventDefault();
            }
          }
        }, { passive: false });

        media.addEventListener('touchend', function (event) {
          if (!tracking) return;

          tracking = false;

          var deltaX = lastX - startX;
          var deltaY = lastY - startY;
          var isSwipe = horizontalGesture
            && Math.abs(deltaX) >= 36
            && Math.abs(deltaX) > Math.abs(deltaY) * 1.15;

          if (!isSwipe) {
            horizontalGesture = false;
            return;
          }

          var currentIndex = parseInt(wrapper.dataset.centecGalleryIndex || '0', 10);
          var direction = deltaX < 0 ? 1 : -1;

          /* Izquierda = foto siguiente / derecha = foto anterior */
          showGalleryImage(wrapper, currentIndex + direction);

          /* Bloquea el click del enlace que suele producirse justo después del swipe. */
          wrapper.__centecLastSwipeAt = Date.now();

          if (event.cancelable) {
            event.preventDefault();
          }

          event.stopPropagation();
          horizontalGesture = false;
        }, { passive: false });

        media.addEventListener('touchcancel', function () {
          tracking = false;
          horizontalGesture = false;
        }, { passive: true });

        if (link) {
          link.addEventListener('click', function (event) {
            var lastSwipeAt = wrapper.__centecLastSwipeAt || 0;

            if (Date.now() - lastSwipeAt < 450) {
              event.preventDefault();
              event.stopPropagation();
              event.stopImmediatePropagation();
            }
          }, true);
        }
      });
    }

    document.addEventListener('change', function (event) {
      if (!(event.target instanceof Element)) return;

      var swatch = event.target.closest('.color-swatch__radio[data-variant-featured-media]');
      if (!swatch) return;

      var card = swatch.closest('product-item');
      var wrapper = card ? card.querySelector('[data-centec-card-gallery]') : null;
      var gallery = getGalleryData(wrapper);
      var mediaId = String(swatch.dataset.variantFeaturedMedia || '');

      if (!wrapper || !mediaId || gallery.length < 2) return;

      var index = gallery.findIndex(function (item) {
        return String(item.id) === mediaId;
      });

      if (index >= 0) {
        showGalleryImage(wrapper, index);
      }
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        initializeGalleryPositions(document);
        bindGalleryButtons(document);
        bindGallerySwipe(document);
      }, { once: true });
    } else {
      initializeGalleryPositions(document);
      bindGalleryButtons(document);
      bindGallerySwipe(document);
    }

    document.addEventListener('shopify:section:load', function (event) {
      initializeGalleryPositions(event.target || document);
      bindGalleryButtons(event.target || document);
      bindGallerySwipe(event.target || document);
    });
  })();

/* CENTEC search v3 — substring search over the public, paginated storefront catalog. */
(() => {
  'use strict';
  if (window.CentecSearchMode === 'native' || window.CentecSearch?.version === 5) return;
  const normalize = value => String(value ?? '').normalize('NFKD').replace(/\p{M}/gu, '').toLocaleLowerCase('es').replace(/[^\p{L}\p{N}]+/gu, ' ').trim().replace(/\s+/g, ' ');
  const compact = value => normalize(value).replace(/ /g, '');
  const esc = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const root = () => window.Shopify?.routes?.root || '/';
  const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'es-CL', {style:'currency', currency:currency, maximumFractionDigits:currency === 'CLP' ? 0 : 2}).format(Number(cents) / 100);
  let currency = 'CLP';
  function prepare(product) {
    const identifiers = (product.variants || []).flatMap(v => [v.sku, v.barcode]).filter(Boolean).map(compact);
    const fields = [product.title, product.vendor, product.type, ...(product.tags || []), ...(product.variants || []).flatMap(v => [v.sku, v.barcode, v.title])].filter(Boolean).map(normalize);
    return {...product, _fields:fields, _compact:fields.map(compact), _identifiers:identifiers, _title:normalize(product.title)};
  }
  function score(product, query) {
    const text = normalize(query);
    if (!text) return 0;
    const packed = compact(query);
    const tokens = text.split(' ');
    const perWord = tokens.every(token => product._fields.some(field => field.includes(token)));
    const joined = product._compact.some(field => field.includes(packed));
    if (!perWord && !joined) return 0;
    if (product._identifiers.includes(packed)) return 1000;
    if (product._title === text) return 900;
    if (product._identifiers.some(field => field.startsWith(packed))) return 800 + (product._title.includes(text) ? 100 : 0);
    if (product._identifiers.some(field => field.includes(packed))) return 700 + (product._title.includes(text) ? 100 : 0);
    if (product._title.startsWith(text)) return 600;
    if (product._title.includes(text)) return 500;
    return joined ? 400 : 300;
  }
  function search(products, query) {
    return products.map(product => ({product,score:score(product,query)})).filter(item => item.score > 0).sort((a,b) => Number(b.product.available)-Number(a.product.available) || b.score-a.score || a.product.title.localeCompare(b.product.title,'es')).map(item => item.product);
  }
  const aborted = () => new DOMException('Aborted','AbortError');
  function waitFor(promise, signal) {
    if (!signal) return promise;
    if (signal.aborted) return Promise.reject(aborted());
    return new Promise((resolve,reject) => {
      const cancel = () => reject(aborted());
      signal.addEventListener('abort',cancel,{once:true});
      promise.then(value => {signal.removeEventListener('abort',cancel); if(signal.aborted) reject(aborted()); else resolve(value);}, error => {signal.removeEventListener('abort',cancel);reject(error);});
    });
  }
  let catalogPromise, catalogTime = 0;
  const cacheKey = () => 'centec-search-v3-sections:'+String(window.Shopify?.theme?.id || new URL(location.href).searchParams.get('preview_theme_id') || 'main')+':'+root()+':'+document.documentElement.lang;
  function indexUrl(page) {
    const url = new URL(root()+'collections/all',location.origin);
    url.searchParams.set('section_id','centec-search-index');
    url.searchParams.set('sort_by','title-ascending');
    url.searchParams.set('page',String(page));
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id',preview);
    return url;
  }
  async function loadPage(page) {
    let response;
    for(let attempt=0;attempt<2;attempt++) {
      response = await fetch(indexUrl(page), {credentials:'same-origin',signal:AbortSignal.timeout(18000)});
      if(response.ok || ![429,500,502,503,504].includes(response.status) || attempt===1) break;
      await response.body?.cancel();
      await new Promise(resolve=>setTimeout(resolve,1000));
    }
    if(!response.ok) throw new Error('Catálogo no disponible ('+response.status+')');
    const doc = new DOMParser().parseFromString(await response.text(),'text/html');
    const payload = doc.querySelector('script[data-centec-search-index]');
    if(!payload) throw new Error('La API de secciones no devolvió el índice');
    const data = JSON.parse(payload.textContent);
    if(data.version !== 3 || data.page !== page || !Array.isArray(data.products) || !Number.isInteger(data.pages) || !Number.isInteger(data.total)) throw new Error('Índice de catálogo inválido');
    return data;
  }
  async function loadCatalog() {
    const first = await loadPage(1);
    if(first.total > 25000 || first.pages > 100) throw new Error('El catálogo supera la cobertura del índice');
    currency = first.currency || 'CLP';
    const chunks = [first];
    let next = 2;
    await Promise.all(Array.from({length:Math.min(4,Math.max(0,first.pages-1))},async () => {
      while(next <= first.pages) {
        const page = next++;
        const chunk = await loadPage(page);
        if(chunk.pages !== first.pages || chunk.total !== first.total) throw new Error('El catálogo cambió durante la búsqueda; vuelve a intentarlo');
        chunks.push(chunk);
      }
    }));
    const all = chunks.sort((a,b)=>a.page-b.page).flatMap(chunk=>chunk.products);
    const unique = new Map(all.map(product=>[product.id,product]));
    if(unique.size !== first.total || all.length !== first.total) throw new Error('No se pudo completar el catálogo; vuelve a intentarlo');
    catalogTime = Date.now();
    try { sessionStorage.setItem(cacheKey(),JSON.stringify({version:3,time:catalogTime,currency,products:[...unique.values()]})); } catch (_) { /* Storage can be disabled or full. */ }
    return [...unique.values()].map(prepare);
  }
  function catalog(signal) {
    if(!catalogPromise) {
      try {
        const cached=JSON.parse(sessionStorage.getItem(cacheKey()));
        if(cached?.version===3 && Date.now()-cached.time>=0 && Date.now()-cached.time<120000 && Array.isArray(cached.products)) {
          currency=cached.currency || 'CLP';catalogTime=cached.time;catalogPromise=Promise.resolve(cached.products.map(prepare));
        }
      } catch (_) { /* Fall through to the complete live catalog. */ }
    }
    if(!catalogPromise || (catalogTime && Date.now()-catalogTime > 120000)) {
      catalogTime = 0;
      const pending = loadCatalog();
      catalogPromise = pending;
      pending.catch(()=>{if(catalogPromise===pending) catalogPromise=null;});
    }
    return waitFor(catalogPromise,signal);
  }
  async function partialPredictive(url, query, signal) {
    const products = search(await catalog(signal),query).slice(0,6).map(p=>({...p,price:Number(p.price)/100,featured_image:p.image?{url:p.image}:null}));
    return {query,data:{resources:{results:{products,collections:[]}}}};
  }
  const numericQuery = query => /^\d{3,64}$/.test(String(query).trim());
  const identifierQuery = query => /\d/.test(query)&&/^[\p{L}\p{N}._/-]{3,64}$/u.test(query);
  function hasFilters(url){return [...url.searchParams].some(([key,value])=>key.startsWith('filter.')&&value!=='');}
  function usePartial(query,nativeCount,url){return numericQuery(query)||(nativeCount===0&&!hasFilters(url)&&normalize(query).length>=3);}
  async function predictive(url,query,signal){
    if(numericQuery(query))return partialPredictive(url,query,signal);
    try{
      const response=await fetch(url,{signal,credentials:'same-origin'});
      if(!response.ok)throw Error('Búsqueda no disponible ('+response.status+')');
      const data=await response.json();
      if(!data.resources?.results?.products?.length&&identifierQuery(query)){
        const partial=await partialPredictive(url,query,signal);
        if(partial.data.resources.results.products.length)return partial;
      }
      return {query,data};
    }catch(error){
      if(error.name!=='AbortError'&&identifierQuery(query))return partialPredictive(url,query,signal);
      throw error;
    }
  }
  const markerSelector = '[data-centec-search-summary]';
  let active, currentQuery = '', currentProducts = [];
  function closeSuggestions() {
    document.querySelectorAll('[data-centec-search-results]').forEach(node=>{node.hidden=true;});
    document.querySelectorAll('[data-centec-search-input]').forEach(node=>node.setAttribute('aria-expanded','false'));
  }
  // Keep the existing theme's Liquid section, product-item snippet and facet controls.
  const contentSelector='[data-centec-search-content]';
  const path=value=>new URL(value,location.href).pathname;
  const idQuery=products=>products.length?products.map(p=>'id:'+p.id).join(' OR '):'id:0';
  const list=value=>Array.isArray(value)?value:value?[value]:[];
  function filterState(url){
    return {vendor:url.searchParams.getAll('filter.p.vendor'),type:url.searchParams.getAll('filter.p.product_type'),availability:url.searchParams.getAll('filter.v.availability'),min:url.searchParams.get('filter.v.price.gte')||'',max:url.searchParams.get('filter.v.price.lte')||'',sort:url.searchParams.get('sort_by')||'relevance',page:Math.max(1,Math.floor(Number(url.searchParams.get('page'))||1))};
  }
  function filtered(products,state){
    const vendors=list(state.vendor),types=list(state.type),availability=list(state.availability);
    let output=products.filter(p=>(!vendors.length||vendors.includes(p.vendor))&&(!types.length||types.includes(p.type))&&(!availability.length||availability.includes(p.available?'1':'0'))&&(state.min==null||state.min===''||p.price>=Number(state.min)*100)&&(state.max==null||state.max===''||p.price<=Number(state.max)*100));
    const sorts={'price-ascending':(a,b)=>a.price-b.price,'price-descending':(a,b)=>b.price-a.price,'title-ascending':(a,b)=>a.title.localeCompare(b.title,'es'),'title-descending':(a,b)=>b.title.localeCompare(a.title,'es')};
    if(sorts[state.sort])output=[...output].sort((a,b)=>Number(b.available)-Number(a.available)||sorts[state.sort](a,b));
    return output;
  }
  async function nativeSection(address,query,sectionId,signal,withFilters=true){
    const url=new URL(address);url.searchParams.set('q',query);url.searchParams.set('type','product');url.searchParams.set('options[unavailable_products]','last');url.searchParams.set('section_id',sectionId);url.searchParams.set('page','1');url.searchParams.delete('sections');
    if(!withFilters)[...url.searchParams.keys()].forEach(key=>{if(key.startsWith('filter.'))url.searchParams.delete(key);});
    const response=await fetch(url.href,{signal,credentials:'same-origin'});
    if(!response.ok)throw Error('No se pudo cargar el formato original ('+response.status+')');
    const doc=new DOMParser().parseFromString(await response.text(),'text/html');
    const content=doc.querySelector(contentSelector);
    if(!content)throw Error('La respuesta no contiene la sección original');
    return {doc,content,grid:content.querySelector('.product-list__inner')};
  }
  function cardsIn(grid){return grid?[...grid.children].filter(node=>node.matches('product-item')):[];}
  function cardPath(card){return path(card.querySelector('a[href*="/products/"]')?.getAttribute('href')||'/');}
  function present(content,query,address,total){
    content.querySelectorAll('input[name="q"]').forEach(input=>{input.value=query;input.setAttribute('value',query);});
    const heading=content.querySelector('.centec-search-bar__title');if(heading)heading.textContent='Resultados para “'+query+'”';
    content.querySelectorAll('.centec-search-bar__count,.product-facet__meta-bar-item--count').forEach(node=>{node.textContent=total===1?'1 producto':total+' productos';});
    content.querySelectorAll('a[href]').forEach(link=>{const url=new URL(link.getAttribute('href'),address);if(url.origin===location.origin&&/\/search\/?$/.test(url.pathname)){url.searchParams.set('q',query);url.searchParams.delete('section_id');link.href=url.href;}});
    content.querySelectorAll('script:not([type="application/json"])').forEach(node=>node.remove());
  }
  function hydrateFilters(content,state){
    const groups={'filter.p.vendor':['vendor',p=>p.vendor],'filter.p.product_type':['type',p=>p.type],'filter.v.availability':['availability',p=>p.available?'1':'0']};
    for(const [name,[key,getValue]]of Object.entries(groups)){
      const candidates=filtered(currentProducts,{...state,[key]:[],sort:'relevance'});
      content.querySelectorAll('input[type="checkbox"]').forEach(input=>{
        if(input.name!==name)return;
        const count=candidates.filter(p=>getValue(p)===input.value).length;
        input.checked=list(state[key]).includes(input.value);input.disabled=count===0&&!input.checked;
        const label=content.querySelector('label[for="'+CSS.escape(input.id)+'"]');
        if(label&&label.children.length===0)label.textContent=name==='filter.v.availability'?(input.value==='1'?'Disponibles ('+count+')':'Agotados'):input.value+' ('+count+')';
        input.closest('.color-swatch,.block-swatch')?.classList.toggle('is-disabled',input.disabled);
      });
    }
    const maximum=Math.ceil(Math.max(0,...currentProducts.map(p=>Number(p.price)||0))/100);
    const price=content.querySelector('price-range');
    if(price){
      const from=price.querySelector('input[name="filter.v.price.gte"]'),to=price.querySelector('input[name="filter.v.price.lte"]');
      if(from){from.value=state.min;from.max=state.max||maximum;}
      if(to){to.value=state.max;to.max=maximum;to.placeholder=maximum;}
      const ranges=[...price.querySelectorAll('input[type="range"]')];
      ranges.forEach((input,i)=>{input.max=maximum;input.value=i?(state.max||maximum):(state.min||0);});
      const group=price.querySelector('.range-group');
      if(group){group.style.setProperty('--range-min',maximum?100*Number(state.min||0)/maximum+'%':'0%');group.style.setProperty('--range-max',maximum?100*Number(state.max||maximum)/maximum+'%':'100%');}
    }
    // Selected groups stay open; the original section continues to manage the brand group's default state.
    content.querySelectorAll('input[type="checkbox"]:checked').forEach(input=>{
      const collapse=input.closest('collapsible-content');if(!collapse)return;
      collapse.setAttribute('open','');const trigger=content.querySelector('[aria-controls="'+CSS.escape(collapse.id)+'"]');trigger?.setAttribute('aria-expanded','true');
    });
  }
  function pagination(content,address,page,pages){
    const old=content.querySelector('page-pagination');
    const previous=old?.querySelector('[rel="prev"]')?.innerHTML||'‹';
    const next=old?.querySelector('[rel="next"]')?.innerHTML||'›';
    old?.remove();if(pages<=1)return;
    const target=content.querySelector('.product-facet__main');if(!target)return;
    const item=(number,label,relation='')=>{const url=new URL(address);url.searchParams.set('page',number);return '<a class="pagination__nav-item heading heading--small" data-page="'+number+'" href="'+esc(url.href)+'"'+(relation?' rel="'+relation+'"':'')+' aria-label="Página '+number+'">'+label+'</a>';};
    const numbers=[...new Set([1,...Array.from({length:5},(_,i)=>page-2+i),pages])].filter(n=>n>=1&&n<=pages).sort((a,b)=>a-b);
    let links=page>1?item(page-1,previous,'prev'):'';let before=0;
    for(const n of numbers){if(before&&n>before+1)links+='<span class="pagination__nav-item heading heading--small">…</span>';links+=n===page?'<span class="pagination__nav-item heading heading--small" aria-current="page">'+n+'</span>':item(n,n);before=n;}
    if(page<pages)links+=item(page+1,next,'next');
    target.insertAdjacentHTML('beforeend','<page-pagination ajax class="pagination"><nav class="pagination__nav">'+links+'</nav></page-pagination>');
  }
  async function renderNative(marker,address,signal,query){
    const state=filterState(address),matches=filtered(currentProducts,state);
    const size=48,pages=Math.max(1,Math.ceil(matches.length/size)),page=Math.min(state.page,pages);
    const visible=matches.slice((page-1)*size,page*size);
    const fullQuery=idQuery(currentProducts);
    // Large match sets use a normal theme shell and request only the current page's IDs.
    // No unbounded ID list is sent in a URL.
    // A broad query needs a full facet shell and the relevance-ordered page; fetch them together.
    const [shell,pageSection]=await Promise.all([
      nativeSection(address,fullQuery.length<=5000?fullQuery:'*',marker.dataset.searchSection,signal),
      currentProducts.length>size&&visible.length?nativeSection(address,idQuery(visible),marker.dataset.searchSection,signal,false):null
    ]);
    let section=shell;
    if(!section.grid&&currentProducts.length)section=await nativeSection(address,fullQuery.length<=5000?fullQuery:'*',marker.dataset.searchSection,signal,false);
    if(section.grid){
      const wanted=new Set(visible.map(p=>path(p.url)));
      let cardSection=pageSection||section;
      const existing=new Map(cardsIn(section.grid).map(card=>[cardPath(card),card]));
      if(!pageSection&&visible.some(p=>!existing.has(path(p.url))))cardSection=await nativeSection(address,idQuery(visible),marker.dataset.searchSection,signal,false);
      const found=new Map(cardsIn(cardSection.grid).map(card=>[cardPath(card),card]));
      const nativeCards=visible.map(p=>found.get(path(p.url)));
      if(nativeCards.some(card=>!card))throw Error('No se pudieron recuperar todas las tarjetas originales');
      const styles=[...cardSection.grid.children].filter(node=>node.tagName==='STYLE');
      nativeCards.forEach(card=>card.removeAttribute('reveal'));
      section.grid.replaceChildren(...styles.slice(0,1),...nativeCards);
      section.grid.removeAttribute('data-centec-stock-grid');
      section.grid.closest('product-list')?.removeAttribute('stagger-apparition');
      if(!visible.length){const empty=section.doc.createElement('div');empty.className='empty-state';empty.textContent='No se han encontrado resultados.';section.grid.append(empty);}
    }
    present(section.content,query,address,matches.length);hydrateFilters(section.content,state);pagination(section.content,address,page,pages);
    if(signal.aborted)throw aborted();
    const live=marker.querySelector(contentSelector);
    const drawer=live.querySelector('#facet-filters'),freshDrawer=section.content.querySelector('#facet-filters');
    const main=live.querySelector('#facet-main'),freshMain=section.content.querySelector('#facet-main');
    if(drawer&&freshDrawer&&main&&freshMain){
      // Keep the native drawer connected: its focus trap, scroll lock and toolbar state belong to this element.
      const scrollTop=drawer.querySelector('.drawer__content')?.scrollTop||0;
      const focus=document.activeElement;
      const focusedField=drawer.contains(focus)?{name:focus.name,value:focus.value}:null;
      drawer.querySelectorAll('.collapsible-toggle[aria-controls]').forEach(toggle=>{
        const id=toggle.getAttribute('aria-controls');
        const next=freshDrawer.querySelector('[aria-controls="'+CSS.escape(id)+'"]');
        const collapse=freshDrawer.querySelector('#'+CSS.escape(id));
        if(!next||!collapse)return;
        const expanded=toggle.getAttribute('aria-expanded')==='true';
        next.setAttribute('aria-expanded',String(expanded));collapse.toggleAttribute('open',expanded);
      });
      drawer.replaceChildren(...freshDrawer.childNodes);
      const scroller=drawer.querySelector('.drawer__content');if(scroller)scroller.scrollTop=scrollTop;
      main.replaceWith(freshMain);
      live.querySelector('.centec-search-bar')?.replaceWith(section.content.querySelector('.centec-search-bar'));
      if(drawer.hasAttribute('open')&&focusedField){
        const field=[...drawer.querySelectorAll('input')].find(input=>input.name===focusedField.name&&input.value===focusedField.value);
        field?.focus({preventScroll:true});
      }
    }else{
      // Release native focus/scroll state before removing an open drawer (e.g. an empty result).
      if(drawer?.hasAttribute('open'))drawer.open=false;
      live.replaceChildren(...section.content.childNodes);
    }
    document.querySelector('.mobile-toolbar__item--filters')?.classList.toggle('has-filters',Boolean(live.querySelector('.product-facet__active-list .tag')));
    marker.dataset.searchCount=String(matches.length);marker.dataset.searchPerformed='true';
    document.querySelectorAll('.mobile-toolbar__item--filters,.mobile-toolbar__item--sort').forEach(item=>{item.style.display=matches.length?'flex':'';});
    marker.dispatchEvent(new CustomEvent('shopify:section:load',{bubbles:true,detail:{sectionId:marker.dataset.searchSection}}));
  }
  async function showResults(address,mode='push'){
    const marker=document.querySelector(markerSelector);if(!marker)return;
    const url=new URL(address,location.href);if(url.origin!==location.origin||!/\/search\/?$/.test(url.pathname))return;
    const query=url.searchParams.get('q')||'';if(!normalize(query))return;
    marker.dataset.searchEngine='partial';
    active?.abort();const controller=new AbortController();active=controller;
    const replacing=!currentQuery||normalize(query)!==normalize(currentQuery);
    marker.removeAttribute('data-search-failed');
    if(replacing){
      marker.setAttribute('data-search-initial','');
      const heading=marker.querySelector('.centec-search-bar__title');if(heading)heading.textContent='Resultados para “'+query+'”';
      marker.querySelectorAll('input[name="q"]').forEach(input=>{input.value=query;});
    }
    document.dispatchEvent(new Event('centec:search-start'));
    const status=marker.querySelector('[data-centec-search-status]');
    marker.setAttribute('aria-busy','true');marker.setAttribute('data-search-pending','');
    if(status){status.hidden=false;status.textContent='Buscando productos…';}
    try{
      const products=await catalog(controller.signal);if(controller.signal.aborted||active!==controller)return;
      currentProducts=search(products,query);
      const focused=document.activeElement,queryFocused=marker.contains(focused)&&focused.name==='q';
      await renderNative(marker,url,controller.signal,query);if(controller.signal.aborted||active!==controller)return;
      currentQuery=query;
      if(queryFocused)marker.querySelector('input[name="q"]')?.focus({preventScroll:true});
      document.querySelectorAll('[data-centec-search-input]').forEach(input=>{input.value=query;});closeSuggestions();
      if(mode==='push'&&url.href!==location.href)history.pushState({centecSearch:true},'',url);
      document.title='Resultados para “'+query+'” – CENTEC';if(status){status.hidden=true;status.textContent='';}
    }catch(error){
      if(error.name!=='AbortError'&&active===controller&&status){
        console.warn('CENTEC: no se pudo completar la búsqueda.',error);
        if(replacing&&Number(marker.dataset.searchNativeCount)>0){marker.dataset.searchEngine='native';status.hidden=true;status.textContent='';}
        else {if(replacing)marker.setAttribute('data-search-failed','');status.hidden=false;status.textContent='No pudimos completar la búsqueda. Vuelve a intentarlo.';}
      }
    }finally{
      if(active===controller){marker.removeAttribute('data-search-pending');if(!replacing||!controller.signal.aborted)marker.removeAttribute('data-search-initial');marker.setAttribute('aria-busy','false');if(controller.signal.aborted&&status){status.hidden=true;status.textContent='';}active=null;}
    }
  }
  function start(){
    const marker=document.querySelector(markerSelector);if(!marker)return;
    const address=new URL(location.href),query=address.searchParams.get('q')||'';
    marker.dataset.searchEngine='native';
    if(usePartial(query,Number(marker.dataset.searchNativeCount),address))showResults(location.href,'none');
    // New queries submit to Shopify normally. Only an active partial-SKU result uses the custom facet handler.
    for(const name of ['facet:criteria-changed','pagination:page-changed'])document.addEventListener(name,event=>{
      if(marker.dataset.searchEngine!=='partial'||!marker.contains(event.target)||!event.detail?.url)return;
      event.preventDefault();event.stopPropagation();const url=new URL(event.detail.url,location.href);url.searchParams.set('q',currentQuery);showResults(url);
    },true);
    document.addEventListener('click',event=>{
      if(marker.dataset.searchEngine!=='partial')return;
      const link=event.target.closest?.('page-pagination a[href],[data-action="clear-filters"],.product-facet__active-list a[href]');
      if(!link||event.button!==0||event.ctrlKey||event.metaKey||event.shiftKey||event.altKey)return;
      if(!marker.contains(link))return;
      event.preventDefault();event.stopPropagation();const url=new URL(link.href,location.href);url.searchParams.set('q',currentQuery);showResults(url);
    },true);
    document.addEventListener('input',event=>{if(event.target.name==='q'&&active)active.abort();});
    window.addEventListener('popstate',()=>{if(marker.dataset.searchEngine==='partial')showResults(location.href,'none');});
  }
  window.CentecSearch={version:5,mode:'hybrid',transport:'sections-v1',renderer:'native-liquid',normalize,prepare,score,search,catalog,predictive,showResults,filtered,usePartial};
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',start,{once:true});else start();
})();

// Preserve the existing card galleries and summary count after the native facet component swaps its HTML.
(() => {
  if (!['native','hybrid'].includes(window.CentecSearchMode)) return;
  function startNativePresentation() {
    const root = document.querySelector('[data-centec-search-summary]');
    if (!root) return;
    let scheduled = false;
    function sync() {
      scheduled = false;
      const count = root.querySelector('.product-facet__meta-bar-item--count')?.textContent.trim();
      const summary = root.querySelector('.centec-search-bar__count');
      if (count && summary && summary.textContent !== count) summary.textContent = count;
      if (count) root.dataset.searchCount = count.replace(/[^0-9]/g, '');
      if (root.querySelector('[data-centec-gallery-next]:not([data-centec-gallery-bound])')) {
        root.dispatchEvent(new CustomEvent('shopify:section:load', {bubbles:true}));
      }
    }
    new MutationObserver(() => {
      if (!scheduled) { scheduled = true; requestAnimationFrame(sync); }
    }).observe(root, {childList:true, subtree:true});
    sync();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startNativePresentation, {once:true});
  else startNativePresentation();
})();


